import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { MapPin, Save, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import z from 'zod';

import type { AssetDetailType } from '@/common/asset';
import type { OrganizationMember } from '@/common/organization';
import { ROLE } from '@/common/role';
import { DeleteButton } from '@/components/DeleteButton';
import { type LatLng, LocationPickerDialog } from '@/components/map/LocationPickerDialog';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { usePermission } from '@/hooks/use-permission';
import { getMediaUrl } from '@/lib/utils/storage';
import { PRESIGNED_UPLOAD_SCOPE, uploadFileWithPresignedUrl } from '@/queries/uploads/presigned-upload';
import { Badge } from '../../ui/badge';
import { ResponsibleUserPicker } from './ResponsibleUserPicker';
import { Spinner } from '../../ui/spinner';

export type AssetFormProps = {
	asset: AssetDetailType | null;
	onSubmit: (data: z.infer<ReturnType<typeof buildAssetSchema>>) => void;
	onDelete: () => void;
	isSubmitting: boolean;
	/** Called when the user picks a banner while creating a new asset (no id yet). */
	onBannerFileStaged?: (file: File | null) => void;
	/** Organisation whose users can be picked as the responsible specialist. */
	organizationId?: string;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

/**
 * A required coordinate: rejects an empty field (unlike `z.coerce.number`, which
 * silently turns '' into 0) and keeps the value within the valid range.
 */
const coordinateSchema = (t: TFunction, label: string, min: number, max: number) =>
	z
		.union([z.string(), z.number()])
		.transform((value, ctx) => {
			if (value === '') {
				ctx.addIssue({ code: 'custom', message: t('assets.validation.coordinateRequired', { label }) });
				return z.NEVER;
			}
			const parsed = Number(value);
			if (Number.isNaN(parsed)) {
				ctx.addIssue({ code: 'custom', message: t('assets.validation.coordinateInvalid', { label }) });
				return z.NEVER;
			}
			return parsed;
		})
		.pipe(
			z
				.number()
				.min(min, t('assets.validation.coordinateRange', { label, min, max }))
				.max(max, t('assets.validation.coordinateRange', { label, min, max })),
		);

const buildAssetSchema = (t: TFunction) =>
	z.object({
		type: z.string().min(2, t('assets.validation.typeMinLength')),
		imageUrl: z.string(),
		conditionRating: z.coerce
			.number<string | number>()
			.min(1, t('assets.validation.conditionRatingRange'))
			.max(4, t('assets.validation.conditionRatingRange')),
		latitude: coordinateSchema(t, t('common.labels.latitude'), -90, 90),
		longitude: coordinateSchema(t, t('common.labels.longitude'), -180, 180),
		height: z.coerce.number<string | number>(),
		location: z.string().min(1, t('assets.validation.required')),
		managerName: z.string().min(1, t('assets.validation.required')),
		managerContact: z.string().min(1, t('assets.validation.required')),
		managerEmail: z.email(t('assets.validation.emailInvalid')),
		managerRole: z.string().min(1, t('assets.validation.required')),
		// Linked organisation user; null while the specialist is typed by hand
		managerUserId: z.string().nullable(),
	});

export function AssetForm({
	asset,
	onSubmit,
	onDelete,
	isSubmitting,
	onBannerFileStaged,
	organizationId,
	restoreButton,
}: AssetFormProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const hasUpdatePermission = usePermission(ROLE.ASSETS_UPDATE);
	const inputFile = useRef<HTMLInputElement>(null);
	// Preview object URL used when staging a banner before the asset is created.
	const [stagedPreviewUrl, setStagedPreviewUrl] = useState<string | null>(null);
	const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
	// The linked responsible specialist; name and email are locked to this user while set.
	const [linkedUser, setLinkedUser] = useState<OrganizationMember | null>(asset?.managerUser ?? null);
	const formSchema = useMemo(() => buildAssetSchema(t), [t]);
	const form = useForm({
		resolver: zodResolver(formSchema),
		disabled: !hasUpdatePermission,
		defaultValues: {
			type: asset?.type || '',
			imageUrl: asset?.imageUrl || '',
			conditionRating: asset?.conditionRating || 1,
			latitude: asset?.latitude ?? '',
			longitude: asset?.longitude ?? '',
			height: asset?.height || 0,
			location: asset?.location || '',
			managerName: asset?.managerName || '',
			managerContact: asset?.managerContact || '',
			managerEmail: asset?.managerEmail || '',
			managerRole: asset?.managerRole || '',
			managerUserId: asset?.managerUserId ?? null,
		},
	});
	useRevalidateOnLanguageChange(form);

	// Populate form when asset changes
	useEffect(() => {
		if (asset) {
			form.reset({
				type: asset.type || '',
				imageUrl: asset.imageUrl || '',
				conditionRating: asset.conditionRating || 1,
				latitude: asset.latitude ?? '',
				longitude: asset.longitude ?? '',
				height: asset.height || 0,
				location: asset.location || '',
				managerName: asset.managerName || '',
				managerContact: asset.managerContact || '',
				managerEmail: asset.managerEmail || '',
				managerRole: asset.managerRole || '',
				managerUserId: asset.managerUserId ?? null,
			});
			setLinkedUser(asset.managerUser ?? null);
		}
	}, [asset, form]);

	const handlePickSpecialist = (member: OrganizationMember) => {
		setLinkedUser(member);
		form.setValue('managerUserId', member.id, { shouldDirty: true });
		form.setValue('managerName', member.name, { shouldDirty: true, shouldValidate: true });
		form.setValue('managerEmail', member.email, { shouldDirty: true, shouldValidate: true });
	};

	// Unlinking keeps the values, now editable by hand.
	const handleUnlinkSpecialist = () => {
		setLinkedUser(null);
		form.setValue('managerUserId', null, { shouldDirty: true });
	};

	const handleManualSpecialist = () => {
		handleUnlinkSpecialist();
		form.setFocus('managerName');
	};

	// File upload mutation
	const doFileUploadApi = useMutation({
		mutationFn: async (file: File) => {
			// Implement file upload logic here
			console.log('Uploading file:', file);
			return uploadFileWithPresignedUrl(file, { assetId: asset?.id || '', scope: PRESIGNED_UPLOAD_SCOPE.BANNER });
		},
		onError(error) {
			console.log('File upload error:', error);
		},
		onSuccess({ key }) {
			form.setValue('imageUrl', key);
			toast.success(t('assets.toast.imageUploadSuccess'));
		},
	});

	const handleButtonClick = () => {
		inputFile.current?.click();
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}

		if (!asset?.id) {
			// New asset — stage locally, upload after creation
			const previewUrl = URL.createObjectURL(file);
			if (stagedPreviewUrl) URL.revokeObjectURL(stagedPreviewUrl);
			setStagedPreviewUrl(previewUrl);
			form.setValue('imageUrl', previewUrl);
			onBannerFileStaged?.(file);
			return;
		}

		// Existing asset — upload immediately
		doFileUploadApi.mutate(file);
	};

	const imageUrl = form.watch('imageUrl');

	// Start the picker from the coordinates already in the form, if both are usable.
	const getPickerInitial = (): LatLng | null => {
		const { latitude, longitude } = form.getValues();
		if (latitude === '' || longitude === '') return null;
		const lat = Number(latitude);
		const lng = Number(longitude);
		return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
	};

	const handleLocationPick = ({ lat, lng }: LatLng) => {
		const options = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
		form.setValue('latitude', Number(lat.toFixed(6)), options);
		form.setValue('longitude', Number(lng.toFixed(6)), options);
		setIsLocationPickerOpen(false);
	};

	// The page asks for confirmation (with what the delete takes along)
	const handleDeleteClick = () => onDelete();

	return (
		<Form {...form}>
			<LocationPickerDialog
				open={isLocationPickerOpen}
				initial={getPickerInitial()}
				onPick={handleLocationPick}
				onClose={() => setIsLocationPickerOpen(false)}
			/>
			<form
				noValidate
				data-entity-form
				className="space-y-6 max-w-full"
				onSubmit={form.handleSubmit(onSubmit)}
				onError={(errors) => {
					console.log(errors);
				}}
			>
				<Card className="rounded-lg border shadow-sm">
					<CardContent className="space-y-4">
						<div className="h-50 w-full rounded-md relative border-dashed border-2 group hover:border-zinc-400">
							{doFileUploadApi.isPending && (
								<div className="absolute h-full w-full z-20 flex flex-col items-center justify-center bg-black/50 rounded-md">
									<Badge className="text-white text-lg">
										<Spinner className="size-6!" />
										{t('assets.form.processing')}
									</Badge>
								</div>
							)}
							{!imageUrl && hasUpdatePermission && (
								// biome-ignore lint/a11y/noStaticElementInteractions: needed
								// biome-ignore lint/a11y/useKeyWithClickEvents: needed
								<div
									className="h-full flex flex-col items-center justify-center gap-2 text-zinc-400 group-hover:text-zinc-500  cursor-pointer"
									onClick={handleButtonClick}
								>
									<div className="p-3 bg-white dark:bg-muted-foreground/50 rounded-full border border-muted shadow-sm">
										<Upload />
									</div>
									<span className="text-xs font-medium">{t('assets.form.uploadCoverPhoto')}</span>
								</div>
							)}
							{imageUrl && hasUpdatePermission && (
								// biome-ignore lint/a11y/noStaticElementInteractions: needed
								// biome-ignore lint/a11y/useKeyWithClickEvents: needed
								<div
									className="absolute h-full w-full z-10 flex flex-col items-center justify-center group-hover:bg-black/40 group-hover:opacity-100 rounded-md opacity-0 transition cursor-pointer"
									onClick={handleButtonClick}
								>
									<div className="flex items-center gap-2 text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded-full">
										<Upload />
										{t('assets.form.changePhoto')}
									</div>
								</div>
							)}
							{imageUrl && (
								<img
									crossOrigin="anonymous"
									src={getMediaUrl(imageUrl)}
									aria-label={t('assets.form.imageAlt')}
									className="h-full w-full object-cover rounded-md"
								/>
							)}
							<Input
								className="hidden"
								type="file"
								onChange={(file) => handleFileChange(file)}
								ref={inputFile}
								name="imageUrl"
							/>
						</div>
						<div className="grid grid-cols-4 gap-3">
							<div className="col-span-2">
								<FormField
									control={form.control}
									name="type"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.type')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													className="bg-white"
													placeholder={t('assets.form.assetTypePlaceholder')}
													{...field}
													required
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-2">
								<FormField
									control={form.control}
									name="conditionRating"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<span className="hidden md:inline">{t('assets.form.conditionRating')}</span>
												<span className="inline md:hidden">{t('assets.form.conditionRatingShort')}</span> (1.0-4.0)
											</FormLabel>
											<FormControl>
												<Input type="number" className="bg-white" min={1} max={4} step={0.1} {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-[1fr_1fr_auto_1fr] gap-4 items-end">
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="latitude"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.latitude')}</FormLabel>
											<FormControl>
												<Input type="number" className="bg-white" step={0.000001} {...field} required />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="longitude"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.longitude')}</FormLabel>
											<FormControl>
												<Input type="number" className="bg-white" step={0.000001} {...field} required />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								type="button"
								variant="outline"
								size="icon"
								className="bg-white"
								title={t('assets.form.pickOnMap')}
								aria-label={t('assets.form.pickOnMap')}
								disabled={!hasUpdatePermission}
								onClick={() => setIsLocationPickerOpen(true)}
							>
								<MapPin />
							</Button>
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="height"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.height')}</FormLabel>
											<FormControl>
												<Input type="number" className="bg-white" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div>
							<FormField
								control={form.control}
								name="location"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('assets.form.locationName')}</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="bg-white"
												placeholder={t('common.labels.location')}
												{...field}
												required
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<Separator />

						<p className="text-sm font-semibold text-muted-foreground">{t('assets.form.responsibleSpecialist')}</p>

						<ResponsibleUserPicker
							organizationId={organizationId}
							linkedUser={linkedUser}
							disabled={!hasUpdatePermission}
							onPick={handlePickSpecialist}
							onManual={handleManualSpecialist}
							onUnlink={handleUnlinkSpecialist}
						/>

						<div className="grid grid-cols-2 gap-3">
							<div>
								<FormField
									control={form.control}
									name="managerName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.name')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													className={linkedUser ? 'bg-muted' : 'bg-white'}
													placeholder={t('common.labels.name')}
													{...field}
													readOnly={!!linkedUser}
													required
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div>
								<FormField
									control={form.control}
									name="managerRole"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.role')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													className="bg-white"
													placeholder={t('common.labels.role')}
													{...field}
													required
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div>
								<FormField
									control={form.control}
									name="managerEmail"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.email')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													className={linkedUser ? 'bg-muted' : 'bg-white'}
													placeholder={t('common.labels.email')}
													{...field}
													readOnly={!!linkedUser}
													required
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div>
								<FormField
									control={form.control}
									name="managerContact"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('assets.form.phone')}</FormLabel>
											<FormControl>
												<Input
													type="text"
													className="bg-white"
													placeholder={t('assets.form.phone')}
													{...field}
													required
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</CardContent>
					<CardFooter className="border-t flex justify-between items-center pt-4 lg:pt-6">
						<div>
							{restoreButton}
							<Restricted to={ROLE.ASSETS_DELETE}>
								{asset?.id && (
									<DeleteButton
										onClick={handleDeleteClick}
										pending={isSubmitting}
										className="shadow-sm h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
									/>
								)}
							</Restricted>
						</div>

						<div className="flex gap-1.5 lg:gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="bg-white h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
								onClick={() => navigate(asset?.id ? `/?selected=${asset.id}` : '/')}
							>
								{t('common.actions.cancel')}
							</Button>
							<Button
								type="submit"
								size="sm"
								className="cursor-pointer h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
								disabled={isSubmitting}
								hidden={!hasUpdatePermission}
							>
								{!isSubmitting && <Save className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
								{!isSubmitting && (asset ? t('assets.form.saveChanges') : t('assets.form.createAsset'))}
								{isSubmitting && <Spinner className="ml-1" />}
								{isSubmitting && (asset ? t('common.states.saving') : t('assets.form.creating'))}
							</Button>
						</div>
					</CardFooter>
				</Card>
			</form>
		</Form>
	);
}
